import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { widgetDefault } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/ReveniWidgetConfigurationPage/constants'
import { getTranslations } from '@/configuration/pages/ConfigurationPage/helpers'
import configurationServices from '@/configuration/services/services'

import { getWidgetPayload } from './helpers'

const useReturnWidgetConfiguration = ({ storeId, supportedLanguages, storeProvider, setToastInfo }) => {
  const { t } = useTranslation()
  const {
    data: widgetPreferences,
    loading: gettingWidgetPreferences,
    fetchData: refetchWidgetPreferences,
  } = useFetch(() => configurationServices.getWidgetPreferences({ storeId }), {
    doInitialRequest: true,
  })
  const { fetchData: editWidgetPreferences, loading: applyingWidgetPreferences } = useFetch(
    configurationServices.editWidgetPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [backgroundColor, setBackgroundColor] = useState(
    widgetPreferences?.background_color || widgetDefault.background
  )
  const [textColor, setTextColor] = useState(widgetPreferences?.text_color || widgetDefault.text)
  const [linkColor, setLinkColor] = useState(widgetPreferences?.link_color || widgetDefault.link)
  const [customTexts, setCustomTexts] = useState(null)
  const [useCustomText, setUseCustomText] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    if (widgetPreferences) {
      setBackgroundColor(widgetPreferences?.background_color || widgetDefault.background)
      setTextColor(widgetPreferences?.text_color || widgetDefault.text)
      setLinkColor(widgetPreferences?.link_color || widgetDefault.link)
      setCustomTexts(getTranslations(supportedLanguages, widgetPreferences?.text))
      setUseCustomText(Object.values(widgetPreferences?.text).some(value => value !== null))
      setHasChanges(false)
    }
  }, [supportedLanguages, widgetPreferences])

  useEffect(() => {
    setInitValues()
  }, [setInitValues, widgetPreferences])

  const revertChanges = () => {
    setInitValues()
  }

  const scriptInfo = `<script defer="defer" type="text/javascript" src="${import.meta.env.VITE_REVENI_WIDGET}?storeId=${storeId}&platform=${storeProvider}"></script>`

  const divInfo = `<div id="reveni-widget"></div>`

  const onSave = async () => {
    const newWidgetPreferences = {
      backgroundColor,
      textColor,
      linkColor,
      useCustomText,
      customTexts,
    }
    const form = getWidgetPayload(newWidgetPreferences, supportedLanguages)

    try {
      await editWidgetPreferences({ storeId, preferences: form })
      refetchWidgetPreferences()
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    widgetPreferences,
    gettingWidgetPreferences,
    editWidgetPreferences,
    refetchWidgetPreferences,
    applyingWidgetPreferences,
    backgroundColor,
    setBackgroundColor: param => {
      setHasChanges(true)
      setBackgroundColor(param)
    },
    textColor,
    setTextColor: param => {
      setHasChanges(true)
      setTextColor(param)
    },
    linkColor,
    setLinkColor: param => {
      setHasChanges(true)
      setLinkColor(param)
    },
    customTexts,
    setCustomTexts: param => {
      setHasChanges(true)
      setCustomTexts(param)
    },
    useCustomText,
    setUseCustomText: param => {
      setHasChanges(true)
      setUseCustomText(param)
    },
    scriptInfo,
    divInfo,
    hasChanges,
    setHasChanges,
    revertChanges,
    onSave,
  }
}

export default useReturnWidgetConfiguration
